import { useRef, useState, useEffect, useCallback } from 'react'
import { useMediaPlayer, formatTime } from './useMediaPlayer'
import { Waveform } from './Waveform'
import { Icon } from '../../../public/icons/Icon.tsx'

export type MediaPlayerVariant = 'audio' | 'video'

// Устройство без наведения (телефон/планшет): бар показывается касанием.
const TOUCH_ONLY = typeof window !== 'undefined' && window.matchMedia?.('(hover: none)').matches

interface MediaPlayerProps {
	src: string
	variant: MediaPlayerVariant
	poster?: string
	title?: string
	className?: string
	onDownload?: () => void
	autoPlay?: boolean
	/** Автозапуск без звука (см. useMediaPlayer.startMuted). */
	startMuted?: boolean
	loop?: boolean
	/** Для audio: цвет прогресса волны */
	accentColor?: string
	/** Разрешить toggle play/pause по клику на видео-body. Default: true */
	clickToPlay?: boolean
	/** Показывать ли кнопку fullscreen. Default: true для video */
	showFullscreen?: boolean
	/** Начальное время воспроизведения в секундах (для resume) */
	initialTime?: number
	/** Класс для самого <video> (video): хозяин задаёт им габариты — лайтбокс,
	 *  сцена студии. Корень плеера получает className. */
	videoClassName?: string
	/** Человек сам переключил звук кнопкой/клавишей M (не автоматика плеера). */
	onUserMuteToggle?: (muted: boolean) => void
	/** Пункт «Скорость» в меню «⋮» (video). Default: true. */
	showSpeed?: boolean
	/** Пункт «Картинка в картинке» в меню «⋮», если браузер умеет. Default: true. */
	showPip?: boolean
	/** Один звучащий источник на страницу (utils/tileSound): зазвучав, плеер
	 *  глушит плитки витрин и соседние плееры, а их звук глушит его. Default:
	 *  true для video (лайтбокс, результаты, модал карточки), false для audio —
	 *  у музыки своя логика. */
	sharedSound?: boolean
	/** Размер ролика по метаданным (video): хозяин задаёт по нему пропорции кадра (сцена студии, 25.09). */
	onMediaSize?: (width: number, height: number) => void
	/** Ролик не открылся (файла нет, ссылка устарела) — хозяин гасит «Скачать» и объясняет (сцена студии). */
	onMediaError?: () => void
}

// ── SVG иконки (inline — чтобы не добавлять в sprite) ─────────────
const PlayIcon = ({ size = 16 }: { size?: number }) => (
	<svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
		<path d="M8 5v14l11-7z" />
	</svg>
)
const PauseIcon = ({ size = 16 }: { size?: number }) => (
	<svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
		<path d="M6 5h4v14H6zM14 5h4v14h-4z" />
	</svg>
)
const MoreIcon = ({ size = 14 }: { size?: number }) => (
	<svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
		<circle cx="12" cy="5" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="12" cy="19" r="2" />
	</svg>
)
const SpeedIcon = ({ size = 15 }: { size?: number }) => (
	<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
		<circle cx="12" cy="12" r="9" /><path d="M10 8l5 4-5 4z" fill="currentColor" stroke="none" />
	</svg>
)
const PipIcon = ({ size = 15 }: { size?: number }) => (
	<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" aria-hidden>
		<rect x="3" y="5" width="18" height="14" rx="2" /><rect x="12" y="11" width="7" height="5" rx="1" fill="currentColor" stroke="none" />
	</svg>
)
// Экспортирован для reuse в SunoReplaceSectionSheet (громкость плеера волны).
export const VolumeIcon = ({ size = 16, muted }: { size?: number; muted?: boolean }) => (
	<svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
		{muted ? (
			<path d="M16.5 12A4.5 4.5 0 0 0 14 7.97v2.21l2.45 2.45a4.5 4.5 0 0 0 .05-.63zM19 12c0 .94-.2 1.82-.54 2.64l1.52 1.52A8.8 8.8 0 0 0 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3 3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25a7.09 7.09 0 0 1-2.25.72v2.06a9.02 9.02 0 0 0 3.69-1.31L19.73 21 21 19.73l-9-9L4.27 3zM12 4 9.91 6.09 12 8.18V4z" />
		) : (
			<path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3A4.5 4.5 0 0 0 14 7.97v8.05a4.5 4.5 0 0 0 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
		)}
	</svg>
)
const DownloadIcon = ({ size = 16 }: { size?: number }) => (
	<svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
		<path d="M12 3v12m0 0-4-4m4 4 4-4M5 21h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
	</svg>
)
const RetryIcon = ({ size = 16 }: { size?: number }) => (
	<svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
		<path
			d="M4 4v5h5M20 20v-5h-5M20 9A8 8 0 0 0 5.3 6M4 15a8 8 0 0 0 14.7 3"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
)

/**
 * Универсальный плеер для audio и video.
 * — audio: показывает waveform (canvas) + controls
 * — video: нативный video-тег + кастомный overlay с controls
 *
 * Ошибка загрузки → placeholder «Не удалось загрузить» + кнопка Повторить.
 */
export default function MediaPlayer({
	src,
	variant,
	poster,
	title,
	className,
	onDownload,
	autoPlay,
	startMuted,
	loop,
	accentColor = '#a78bfa',
	clickToPlay = true,
	showFullscreen = true,
	onMediaSize,
	onMediaError,
	initialTime,
	videoClassName,
	onUserMuteToggle,
	showSpeed = true,
	showPip = true,
	sharedSound = variant === 'video',
}: MediaPlayerProps) {
	const audioRef = useRef<HTMLAudioElement>(null)
	const videoRef = useRef<HTMLVideoElement>(null)
	const rootRef = useRef<HTMLDivElement>(null)
	const mediaRef = variant === 'audio' ? audioRef : videoRef

	const {
		isPlaying,
		duration,
		currentTime,
		volume,
		muted,
		loadState,
		togglePlay,
		seekRatio,
		setVolume,
		toggleMute,
		retry,
	} = useMediaPlayer(mediaRef, src, { initialTime, autoPlay, startMuted, sharedSound })

	const [volumeOpen, setVolumeOpen] = useState(false)
	const [showControls, setShowControls] = useState(variant === 'audio')
	const hideTimer = useRef<number | null>(null)
	const [menuOpen, setMenuOpen] = useState(false)
	const [scrubbing, setScrubbing] = useState(false)

	// Авто-скрытие controls для video через 2.5с после последнего движения мыши
	useEffect(() => {
		if (variant !== 'video') return
		if (hideTimer.current) window.clearTimeout(hideTimer.current)
		if (!showControls || !isPlaying || menuOpen || scrubbing) return
		hideTimer.current = window.setTimeout(() => setShowControls(false), 2500)
		return () => {
			if (hideTimer.current) window.clearTimeout(hideTimer.current)
		}
	}, [showControls, isPlaying, variant, menuOpen, scrubbing])

	const handleMouseMove = () => {
		if (variant !== 'video') return
		setShowControls(true)
	}

	const progressRatio = duration > 0 ? currentTime / duration : 0

	// Fullscreen: Fullscreen API на корне плеера (бар остаётся нашим); iOS
	// Safari API не даёт — там системный полноэкран самого <video>.
	const [isFullscreen, setIsFullscreen] = useState(false)
	const toggleFullscreen = useCallback(() => {
		const el = rootRef.current
		if (!el) return
		if (document.fullscreenElement) {
			void document.exitFullscreen?.()
			return
		}
		if (el.requestFullscreen) {
			void el.requestFullscreen().catch(() => {})
			return
		}
		const v = videoRef.current as (HTMLVideoElement & { webkitEnterFullscreen?: () => void }) | null
		v?.webkitEnterFullscreen?.()
	}, [])
	useEffect(() => {
		const onChange = () => setIsFullscreen(document.fullscreenElement === rootRef.current)
		document.addEventListener('fullscreenchange', onChange)
		return () => document.removeEventListener('fullscreenchange', onChange)
	}, [])

	// «⋮»: скачать (если хозяин дал), скорость, картинка в картинке.
	const [rate, setRate] = useState(1)
	useEffect(() => {
		const v = videoRef.current
		if (v) v.playbackRate = rate
	}, [rate, src])
	// PiP: стандартный API (Chrome/Edge/Firefox) или webkit-режим (Safari).
	// Считаем один раз и без нашего элемента — по документу и пробному <video>.
	const [pipSupported] = useState(() => {
		if (typeof document === 'undefined') return false
		if ('pictureInPictureEnabled' in document && document.pictureInPictureEnabled) return true
		const probe = document.createElement('video') as HTMLVideoElement & {
			webkitSupportsPresentationMode?: (m: string) => boolean
		}
		return !!probe.webkitSupportsPresentationMode?.('picture-in-picture')
	})
	const togglePip = useCallback(() => {
		const v = videoRef.current as
			| (HTMLVideoElement & {
				webkitPresentationMode?: string
				webkitSetPresentationMode?: (m: string) => void
			})
			| null
		if (!v) return
		if (document.pictureInPictureElement) {
			void document.exitPictureInPicture().catch(() => {})
		} else if (v.requestPictureInPicture) {
			void v.requestPictureInPicture().catch(() => {})
		} else if (v.webkitSetPresentationMode) {
			v.webkitSetPresentationMode(v.webkitPresentationMode === 'picture-in-picture' ? 'inline' : 'picture-in-picture')
		}
		setMenuOpen(false)
	}, [])
	// Клик мимо меню — закрыть.
	useEffect(() => {
		if (!menuOpen) return
		const onDown = (e: MouseEvent | TouchEvent) => {
			const root = rootRef.current
			if (root && e.target instanceof Node && root.contains(e.target)) return
			setMenuOpen(false)
		}
		document.addEventListener('mousedown', onDown)
		document.addEventListener('touchstart', onDown)
		return () => {
			document.removeEventListener('mousedown', onDown)
			document.removeEventListener('touchstart', onDown)
		}
	}, [menuOpen])

	// Звук руками: хозяин может запомнить выбор (лайтбокс — на сессию).
	const userToggleMute = useCallback(() => {
		const v = mediaRef.current
		const next = v ? !v.muted : !muted
		toggleMute()
		onUserMuteToggle?.(next)
	}, [mediaRef, muted, toggleMute, onUserMuteToggle])

	// Клавиатура на плеере (корень фокусируемый): пробел/K — play, ←/→ — ±5 с,
	// ↑/↓ — громкость, M — звук, F — во весь экран, Esc — закрыть меню.
	const onKeyDown = (e: React.KeyboardEvent) => {
		if (variant !== 'video') return
		const v = videoRef.current
		if (!v) return
		const k = e.key
		if (k === ' ' || k === 'k' || k === 'K') { e.preventDefault(); togglePlay() }
		else if (k === 'ArrowRight') { e.preventDefault(); seekRatio((v.currentTime + 5) / (v.duration || 1)) }
		else if (k === 'ArrowLeft') { e.preventDefault(); seekRatio((v.currentTime - 5) / (v.duration || 1)) }
		else if (k === 'ArrowUp') { e.preventDefault(); setVolume(Math.min(1, volume + 0.1)) }
		else if (k === 'ArrowDown') { e.preventDefault(); setVolume(Math.max(0, volume - 0.1)) }
		else if (k === 'm' || k === 'M') { e.preventDefault(); userToggleMute() }
		else if (k === 'f' || k === 'F') { e.preventDefault(); toggleFullscreen() }
		else if (k === 'Escape' && menuOpen) { e.stopPropagation(); setMenuOpen(false) }
		setShowControls(true)
	}

	// Прогресс: перетаскивание пальцем/мышью, не только клик.
	const scrubTo = (el: HTMLElement, clientX: number) => {
		const rect = el.getBoundingClientRect()
		seekRatio((clientX - rect.left) / Math.max(1, rect.width))
	}
	const onScrubDown = (e: React.PointerEvent<HTMLDivElement>) => {
		e.stopPropagation()
		e.currentTarget.setPointerCapture(e.pointerId)
		setScrubbing(true)
		scrubTo(e.currentTarget, e.clientX)
	}
	const onScrubMove = (e: React.PointerEvent<HTMLDivElement>) => {
		if (!scrubbing) return
		scrubTo(e.currentTarget, e.clientX)
	}
	const onScrubUp = (e: React.PointerEvent<HTMLDivElement>) => {
		if (!scrubbing) return
		setScrubbing(false)
		try { e.currentTarget.releasePointerCapture(e.pointerId) } catch { /* уже отпущено */ }
	}

	// Error placeholder
	if (loadState === 'error') {
		return (
			<div
				ref={rootRef}
				className={`media-player media-player--${variant} media-player--error${className ? ' ' + className : ''}`}
			>
				<div className="media-player__error">
					<span className="media-player__error-text">Не удалось загрузить</span>
					<button
						type="button"
						className="media-player__error-retry"
						onClick={retry}
						aria-label="Повторить"
					>
						<RetryIcon size={14} />
						<span>Повторить</span>
					</button>
				</div>
			</div>
		)
	}

	// ── AUDIO ─────────────────────────────────────────────────────
	if (variant === 'audio') {
		return (
			<div
				ref={rootRef}
				className={`media-player media-player--audio${className ? ' ' + className : ''}`}
			>
				<audio
					ref={audioRef}
					src={src}
					loop={loop}
					preload="metadata"
				/>

				<button
					type="button"
					className="media-player__play"
					onClick={togglePlay}
					disabled={loadState === 'loading'}
					aria-label={isPlaying ? 'Пауза' : 'Воспроизвести'}
				>
					{isPlaying ? <PauseIcon size={16} /> : <PlayIcon size={16} />}
				</button>

				<div className="media-player__main">
					{title && <div className="media-player__title">{title}</div>}
					<Waveform
						src={src}
						progress={progressRatio}
						onSeek={seekRatio}
						progressColor={accentColor}
					/>
					<div className="media-player__time-row">
						<span className="media-player__time">{formatTime(currentTime)}</span>
						<span className="media-player__time media-player__time--total">{formatTime(duration)}</span>
					</div>
				</div>

				<div className="media-player__actions">
					<div
						className="media-player__volume"
						onMouseEnter={() => setVolumeOpen(true)}
						onMouseLeave={() => setVolumeOpen(false)}
					>
						<button
							type="button"
							className="media-player__icon-btn"
							onClick={toggleMute}
							aria-label={muted ? 'Включить звук' : 'Выключить звук'}
						>
							<VolumeIcon size={16} muted={muted || volume === 0} />
						</button>
						{volumeOpen && (
							<div className="media-player__volume-slider">
								<input
									type="range"
									className="media-player__range"
									style={{ '--v': `${(muted ? 0 : volume) * 100}%` } as React.CSSProperties}
									min={0}
									max={1}
									step={0.05}
									value={muted ? 0 : volume}
									aria-label="Громкость"
									onChange={(e) => setVolume(parseFloat(e.target.value))}
								/>
							</div>
						)}
					</div>
					{onDownload && (
						<button
							type="button"
							className="media-player__icon-btn"
							onClick={onDownload}
							aria-label="Скачать"
						>
							<DownloadIcon size={16} />
						</button>
					)}
				</div>
			</div>
		)
	}

	// ── VIDEO ─────────────────────────────────────────
	// Бар — та же матовая пилюля, что бар действий на карточках (владелец 15.09:
	// один стандарт контролов). Клик по видео — play/pause; клики по бару и
	// меню не всплывают к хозяину (лайтбокс закрывается по клику вокруг).
	const stopClick = (e: React.MouseEvent) => e.stopPropagation()
	const hasMenu = !!onDownload || showSpeed || (showPip && pipSupported)
	const barVisible = showControls || !isPlaying || menuOpen || scrubbing

	return (
		<div
			ref={rootRef}
			tabIndex={0}
			className={`media-player media-player--video${barVisible ? ' media-player--controls-visible' : ''}${isFullscreen ? ' media-player--fullscreen' : ''}${className ? ' ' + className : ''}`}
			onMouseMove={handleMouseMove}
			onMouseEnter={() => setShowControls(true)}
			onMouseLeave={() => isPlaying && !menuOpen && setShowControls(false)}
			onTouchStart={() => setShowControls(true)}
			onKeyDown={onKeyDown}
			onClick={(e) => {
				if (!clickToPlay || e.target !== videoRef.current) return
				// На таче первый тап по ролику только показывает бар (как у
				// мобильных плееров); переключает play тап при видимом баре.
				if (TOUCH_ONLY && !barVisible) {
					setShowControls(true)
					return
				}
				togglePlay()
			}}
		>
			<video
				ref={videoRef}
				src={src}
				poster={poster}
				loop={loop}
				playsInline
				preload="metadata"
				className={`media-player__video${videoClassName ? ' ' + videoClassName : ''}`}
				onLoadedMetadata={onMediaSize ? (e) => onMediaSize(e.currentTarget.videoWidth, e.currentTarget.videoHeight) : undefined}
				onError={onMediaError}
			/>

			{loadState === 'loading' && (
				<div className="media-player__video-spinner" aria-hidden onClick={stopClick}>
					<div className="media-player__spinner" />
				</div>
			)}

			{!isPlaying && loadState === 'ready' && (
				<button
					type="button"
					className="media-player__video-big-play"
					onClick={(e) => { e.stopPropagation(); togglePlay() }}
					aria-label="Воспроизвести"
				>
					<PlayIcon size={26} />
				</button>
			)}

			<div className="media-player__bar" onClick={stopClick} role="group" aria-label="Управление видео">
				<button
					type="button"
					className="media-player__bar-btn media-player__bar-btn--play"
					onClick={togglePlay}
					aria-label={isPlaying ? 'Пауза' : 'Воспроизвести'}
				>
					{isPlaying ? <PauseIcon size={14} /> : <PlayIcon size={14} />}
				</button>

				<span className="media-player__time">
					{formatTime(currentTime)} / {formatTime(duration)}
				</span>

				<div
					className={`media-player__scrub${scrubbing ? ' is-scrubbing' : ''}`}
					role="slider"
					aria-label="Прогресс"
					aria-valuemin={0}
					aria-valuemax={100}
					aria-valuenow={Math.round(progressRatio * 100)}
					onPointerDown={onScrubDown}
					onPointerMove={onScrubMove}
					onPointerUp={onScrubUp}
					onPointerCancel={onScrubUp}
				>
					<div className="media-player__scrub-track" />
					<div className="media-player__scrub-fill" style={{ width: `${progressRatio * 100}%` }} />
					<div className="media-player__scrub-thumb" style={{ left: `${progressRatio * 100}%` }} />
				</div>

				<div
					className="media-player__volume"
					onMouseEnter={() => setVolumeOpen(true)}
					onMouseLeave={() => setVolumeOpen(false)}
				>
					<button
						type="button"
						className={`media-player__bar-btn${!muted && volume > 0 ? ' media-player__bar-btn--on' : ''}`}
						onClick={userToggleMute}
						aria-label={muted ? 'Включить звук' : 'Выключить звук'}
						aria-pressed={!muted}
					>
						<Icon id={muted || volume === 0 ? 'gen-audio-off' : 'gen-audio'} size={13} />
					</button>
					{volumeOpen && (
						<div className="media-player__volume-slider media-player__volume-slider--up">
							<input
								type="range"
								className="media-player__range"
								style={{ '--v': `${(muted ? 0 : volume) * 100}%` } as React.CSSProperties}
								min={0}
								max={1}
								step={0.05}
								value={muted ? 0 : volume}
								aria-label="Громкость"
								onChange={(e) => setVolume(parseFloat(e.target.value))}
							/>
						</div>
					)}
				</div>

				{showFullscreen && (
					<button
						type="button"
						className="media-player__bar-btn"
						onClick={toggleFullscreen}
						aria-label={isFullscreen ? 'Выйти из полного экрана' : 'Во весь экран'}
					>
						<Icon id="gen-expand" size={12} />
					</button>
				)}

				{hasMenu && (
					<button
						type="button"
						className={`media-player__bar-btn${menuOpen ? ' media-player__bar-btn--on' : ''}`}
						onClick={() => setMenuOpen((o) => !o)}
						aria-label="Ещё"
						aria-haspopup="menu"
						aria-expanded={menuOpen}
					>
						<MoreIcon size={14} />
					</button>
				)}
			</div>

			{menuOpen && (
				<div className="media-player__menu" role="menu" onClick={stopClick}>
					{onDownload && (
						<button type="button" role="menuitem" className="media-player__menu-item" onClick={() => { setMenuOpen(false); onDownload() }}>
							<Icon id="gen-download" size={15} />
							Скачать
						</button>
					)}
					{showSpeed && (
						<div className="media-player__menu-item media-player__menu-item--row" role="group" aria-label="Скорость">
							<SpeedIcon size={15} />
							Скорость
							<span className="media-player__speed">
								{[0.5, 1, 1.5, 2].map((r) => (
									<button
										key={r}
										type="button"
										role="menuitemradio"
										aria-checked={rate === r}
										className={`media-player__speed-btn${rate === r ? ' is-on' : ''}`}
										onClick={() => setRate(r)}
									>
										{String(r).replace('.', ',')}×
									</button>
								))}
							</span>
						</div>
					)}
					{showPip && pipSupported && (
						<button type="button" role="menuitem" className="media-player__menu-item" onClick={togglePip}>
							<PipIcon size={15} />
							Картинка в картинке
						</button>
					)}
				</div>
			)}
		</div>
	)
}
